import { Controller } from 'react-hook-form'
import MultiSelectField, { IMultiSelectProps } from './MultiSelectField'
import strings from 'l10n'

export interface ControlledMultiSelectFieldProps
  extends Omit<IMultiSelectProps, 'value' | 'onChange' | 'error' | 'helperText'> {
  /** Name of the field in the form */
  name: string
  /** React Hook Form control object */
  control: any
  /** Whether the field is required */
  required?: boolean
  /** Custom validation rules */
  rules?: any
  /** Custom required message */
  requiredMessage?: string
}

/**
 * MultiSelectField component integrated with react-hook-form Controller
 *
 * This component wraps the base MultiSelectField with react-hook-form's Controller,
 * providing automatic form state management, validation, and error handling.
 *
 * @example
 * ```tsx
 * <ControlledMultiSelectField
 *   name="feeTypes"
 *   control={control}
 *   label="Fee Types"
 *   options={feeTypeOptions}
 *   required
 *   disableCloseOnSelect
 * />
 * ```
 */
export const ControlledMultiSelectField = (props: ControlledMultiSelectFieldProps) => {
  const {
    name,
    control,
    required = false,
    rules = {},
    requiredMessage = strings.FIELD_IS_REQUIRED || 'Field is required',
    ...multiSelectProps
  } = props

  const validationRules = {
    ...rules,
    ...(required && {
      required: requiredMessage,
      validate: {
        notEmpty: (value: any) => {
          if (!value || (Array.isArray(value) && value.length === 0)) {
            return requiredMessage
          }
          return true
        },
        ...rules.validate,
      },
    }),
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={validationRules}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <MultiSelectField
          {...multiSelectProps}
          name={name}
          onChange={(_event, val) => onChange(val)}
          value={value || []}
          error={Boolean(error)}
          helperText={error?.message}
          required={required}
        />
      )}
    />
  )
}

export default ControlledMultiSelectField
